import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input, output, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { catchError, EMPTY, finalize, tap } from 'rxjs';
import { Project } from '@modules/projects/shared/models/project.model';
import { ProjectAPIService } from '@modules/projects/shared/services/project-api.service';
import { SnackBarService } from '@core/services/snackbar.service';
import { publicFile } from '@shared/helpers/public-file.helper';
import { UIModule } from '@shared/modules/ui/ui.module';
import { Employee } from '@shared/models/employee.model';
import { EmployeeSearchItem } from '@shared/models/employee-search-item.model';
import { DEFAULT_PANEL_ROLES } from '@shared/auth/enums/app-role.enum';
import { SkeletonItemComponent } from '@shared/components/skeleton-item/skeleton-item.component';
import { RoleAccessService } from '@shared/auth/services/role-access.service';

@Component({
  selector: 'project-dashboard-header-responsible',
  standalone: true,
  templateUrl: './project-dashboard-header-responsible.component.html',
  styleUrls: ['./project-dashboard-header-responsible.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AngularSvgIconModule, MatMenuModule, TranslateModule, UIModule, SkeletonItemComponent]
})
export class ProjectDashboardHeaderResponsibleComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly projectApiService = inject(ProjectAPIService);
  private readonly snackBarService = inject(SnackBarService);
  private readonly translateService = inject(TranslateService);
  private readonly roleAccessService = inject(RoleAccessService);

  project = input<Project | null>(null);

  projectUpdated = output<Project>();

  protected readonly loading = signal(false);
  protected readonly saving = signal(false);
  protected readonly search = signal('');
  protected readonly employees = signal<Employee[]>([]);

  private searchTimeout: ReturnType<typeof setTimeout> | null = null;

  @ViewChild(MatMenuTrigger) private menuTrigger?: MatMenuTrigger;

  protected readonly responsibleEmployeeName = computed(() => {
    const userPerson = this.project()?.basicDataBox?.responsibleEmployee?.userPerson || this.project()?.creator?.userPerson;
    const previewName = String(userPerson?.previewName || '').trim();
    if (previewName) return previewName;

    return [userPerson?.firstName, userPerson?.lastName].filter(Boolean).join(' ').trim();
  });

  protected readonly responsibleEmployeeAvatar = computed(() => {
    const photo =
      this.project()?.basicDataBox?.responsibleEmployee?.userPerson?.photo || this.project()?.creator?.userPerson?.photo;

    return photo ? publicFile(photo, 100) : null;
  });

  protected readonly visibleEmployees = computed(() => this.employees());

  protected onMenuOpened(): void {
    if (!this.project()?.id || this.saving()) return;
    this.loadEmployees();
  }

  protected onMenuClosed(): void {
    this.search.set('');
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }
  }

  protected onSearchChange(value: string): void {
    this.search.set(value);

    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    this.searchTimeout = setTimeout(() => {
      this.loadEmployees();
    }, 500);
  }

  protected isSelected(employee: Employee): boolean {
    return employee?.id === this.project()?.basicDataBox?.responsibleEmployee?.id;
  }

  protected employeeAvatarUrl(employee: Employee): string | null {
    return employee?.userPerson?.photo ? publicFile(employee.userPerson.photo, 100) : null;
  }

  protected selectEmployee(employee: Employee): void {
    const project = this.project();
    if (!project?.id || !employee?.id || this.isSelected(employee) || this.saving()) return;

    this.saving.set(true);
    this.projectApiService
      .setResponsibleEmployee(project.id, employee.id)
      .pipe(
        tap((res: Project) => {
          const updatedProject = new Project(res);
          this.projectUpdated.emit(updatedProject);
          this.menuTrigger?.closeMenu();
          this.snackBarService.success(this.translateService.instant('Projects.ResponsibleCompany.successSettingResponsible'));
        }),
        catchError(() => {
          this.snackBarService.error(this.translateService.instant('Projects.ResponsibleCompany.errorSettingResponsible'));
          return EMPTY;
        }),
        finalize(() => this.saving.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private loadEmployees(): void {
    this.loading.set(true);
    const requiredId = this.project()?.basicDataBox?.responsibleEmployee?.id;
    this.projectApiService
      .searchResponsibleEmployees(this.search(), requiredId)
      .pipe(
        tap((employees: any[]) => {
          const normalizedEmployees = (employees || [])
            .map((employee) => {
              const employeeSearchItem = new EmployeeSearchItem(employee);
              return new Employee({
                ...employee,
                id: employeeSearchItem.id,
                role: employeeSearchItem.role,
                userPerson: employeeSearchItem.userPerson,
                contacts: employeeSearchItem.contacts
              });
            })
            .filter((employee) =>
              this.roleAccessService.hasAnyRole(DEFAULT_PANEL_ROLES, employee.role)
            );

          const currentResponsible = this.project()?.basicDataBox?.responsibleEmployee
            ? new Employee(this.project()?.basicDataBox?.responsibleEmployee)
            : null;

          const employeesWithCurrentResponsible =
            currentResponsible && !normalizedEmployees.some((employee) => employee.id === currentResponsible.id)
              ? [currentResponsible, ...normalizedEmployees]
              : normalizedEmployees;

          const uniqueEmployees = employeesWithCurrentResponsible.filter(
            (employee, index, list) => !!employee?.id && list.findIndex((item) => item.id === employee.id) === index
          );

          this.employees.set(uniqueEmployees);
        }),
        catchError(() => {
          this.snackBarService.error(this.translateService.instant('Projects.ResponsibleCompany.errorSettingResponsible'));
          return EMPTY;
        }),
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }
}
